import { IGroupedProductByType, IOption, IProduct } from "../types";
import flow from "lodash/fp/flow";
import groupBy from "lodash/fp/groupBy";
import { VALID_ZONE_TYPES } from "../enums";
import { CustomerTypes } from "../../components/createBadge/enums/CustomerTypes";
import MagentoHelper from "../network/helpers/magento/MagentoHelper";
import cloneDeep from "lodash.clonedeep";
import { TFunction } from "i18next";
const map = require("lodash/fp/map").convert({ cap: false });

type GroupProductOptions = {
  includePersonProductsForDelegate?: boolean;
  t?: TFunction<"common">;
  ignoreDateCheck?: boolean;
  isRxContractorChecked?: boolean;
};

const filterRegProducts = (products: IProduct[], gbsCode?: string) => {
  return products
    .filter(pr => (gbsCode ? pr.rx_event_edition_code === gbsCode : true) && pr.__typename === "BundleProduct")
    .filter(pr => pr && Object.keys(pr).length > 0);
};

const handleProductWithMultipleCustomerTypes = (product: IProduct, types: string[]) => {
  return types.map(type => ({ ...product, rx_customer_type: type.trim() }));
};

const getSplittedProductsByCustomerType = (magentoProducts: IProduct[]) => {
  const products: IProduct[] = [];
  magentoProducts.forEach(product => {
    const splitted = product.rx_customer_type?.split(",");
    if (splitted && splitted.length > 1) {
      products.push(...handleProductWithMultipleCustomerTypes(product, splitted));
      return;
    }
    products.push(product);
  });
  return products;
};

const groupProductsByType = (magentoProducts: IProduct[], options?: GroupProductOptions) => {
  // array for tradeVisitor and other types that use Bundles
  const filteredProducts = magentoProducts.filter(product =>
    options?.ignoreDateCheck ? true : MagentoHelper.areProductDatesValid(product)
  );
  const groupedProducts = flow(
    groupBy("rx_customer_type"),
    map((pr: IProduct, customerType: string) => ({
      customerType,
      products: pr
    }))
  )(getSplittedProductsByCustomerType(filteredProducts)) as IGroupedProductByType[];

  if (options?.includePersonProductsForDelegate) {
    return groupedProducts;
  }

  // for delegates and contractors we use Grouped products
  const productsWithoutDelegatesAndContractors = groupedProducts.filter(p =>
    options?.isRxContractorChecked
      ? p.customerType !== CustomerTypes.EXHIBITING_DELEGATE
      : p.customerType !== CustomerTypes.EXHIBITING_DELEGATE && p.customerType !== CustomerTypes.CONTRACTOR
  );

  const exhibitingDelegatesProducts = cloneDeep(MagentoHelper.getGroupedProductsByCustomerType(CustomerTypes.EXHIBITING_DELEGATE)).map(
    parentProduct => {
      const childProduct = magentoProducts.find(pr => pr.sku === parentProduct.rx_entitlement_child);
      if (childProduct) {
        parentProduct.price_range.minimum_price.regular_price.value = MagentoHelper.getProductPrice(childProduct);
        parentProduct.items[0].product.price.regularPrice.adjustments = childProduct.price.regularPrice.adjustments;
      }
      return parentProduct;
    }
  );

  const contractorProducts = cloneDeep(MagentoHelper.getGroupedProductsByCustomerType(CustomerTypes.CONTRACTOR)).map(parentProduct => {
    const childProduct = magentoProducts.find(pr => pr.sku === parentProduct.rx_entitlement_child);
    if (childProduct) {
      parentProduct.price_range.minimum_price.regular_price.value = MagentoHelper.getProductPrice(childProduct);
      parentProduct.items[0].product.price.regularPrice.adjustments = childProduct.price.regularPrice.adjustments;
    }
    return parentProduct;
  });

  const finalProducts = [...productsWithoutDelegatesAndContractors];

  if (exhibitingDelegatesProducts.length > 0) {
    finalProducts.push({ customerType: CustomerTypes.EXHIBITING_DELEGATE, products: exhibitingDelegatesProducts });
  }

  if (contractorProducts.length > 0 && !options?.isRxContractorChecked) {
    finalProducts.push({ customerType: CustomerTypes.CONTRACTOR, products: contractorProducts });
  }

  return finalProducts;
};

const isOptionUnique = (options: IOption[], current: IOption) => {
  return !options.find(option => option.product.sku === current.product.sku);
};

const getValidZones = (products: IProduct[]): IOption[] => {
  const validZones: IOption[] = [];
  products.forEach(product =>
    product.items.forEach(bundleItem =>
      bundleItem.options.forEach(option => {
        const isValidType = (Object.values(VALID_ZONE_TYPES) as string[]).includes(option.product.rx_type);
        if (isValidType && isOptionUnique(validZones, option)) {
          validZones.push(option);
        }
      })
    )
  );
  return validZones;
};

const productsUtils = {
  filterRegProducts,
  groupProductsByType,
  getSplittedProductsByCustomerType,
  isOptionUnique,
  getValidZones
};

export default productsUtils;
